import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

export default function useBookCatalog(filters, batchSize = 12) {
  const [state, setState] = useState({ items: [], total: 0, nextOffset: null, busy: true, error: '' });
  const [attempt, setAttempt] = useState(0);
  const generation = useRef(0), pending = useRef(false);
  useEffect(() => {
    const version = ++generation.current;
    pending.current = true;
    setState({ items: [], total: 0, nextOffset: null, busy: true, error: '' });
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get('/api/books/catalog', { params: { ...filters, limit: batchSize, offset: 0 }, signal: controller.signal });
        if (version === generation.current) setState({ items: data.items, total: data.total, nextOffset: data.nextOffset, busy: false, error: '' });
      } catch (error) {
        if (version === generation.current && !controller.signal.aborted) setState(s => ({ ...s, busy: false, error: 'Could not load books. Please try again.' }));
      } finally { if (version === generation.current) pending.current = false; }
    }, 150);
    return () => { clearTimeout(timer); controller.abort(); generation.current = version + 1; };
  }, [filters, batchSize, attempt]);
  const loadMore = useCallback(async () => {
    if (pending.current || state.nextOffset === null) return;
    const version = generation.current;
    pending.current = true;
    setState(s => ({ ...s, busy: true, error: '' }));
    try {
      const { data } = await axios.get('/api/books/catalog', { params: { ...filters, limit: batchSize, offset: state.nextOffset } });
      if (version !== generation.current) return;
      // Start cover downloads while the upcoming cards are still off-screen.
      data.items.forEach(book => { if (book.coverImage) { const image = new Image(); image.src = book.coverImage; } });
      setState(s => {
        const ids = new Set(s.items.map(book => book._id));
        const added = data.items.filter(book => { if (ids.has(book._id)) return false; ids.add(book._id); return true; });
        return { items: [...s.items, ...added], total: data.total, nextOffset: data.nextOffset, busy: false, error: '' };
      });
    } catch {
      if (version === generation.current) setState(s => ({ ...s, busy: false, error: 'Could not load more books. Please try again.' }));
    } finally { if (version === generation.current) pending.current = false; }
  }, [filters, batchSize, state.nextOffset]);
  const retry = () => state.nextOffset === null ? setAttempt(n => n + 1) : loadMore();
  const showLess = () => setState(s => ({ ...s, items: s.items.slice(0, batchSize), nextOffset: batchSize < s.total ? batchSize : null }));
  return { ...state, loadMore, showLess, retry };
}
